/**
 * connectionRuntimeConfigService — Sprint 17.
 *
 * Single authoritative source for Firebase connection credentials used by
 * Verify Connection, Deep Test, Certification, and the Connection Runtime Engine.
 *
 * NEVER reads apiKey (or other secrets) from UI-facing connection models.
 * Always resolves the connection id through getConnectionSecrets (Studio Firestore).
 */
import { getConnectionSecrets } from "../services/connectionService";
import { buildFirebaseConfig, isMaskedOrEmpty } from "../services/connectionVerifyUtils";
import { SECRET_MASK } from "../constants";

/**
 * Extract a stable connection id from a string id or partial UI model.
 * @returns {string|null}
 */
export function extractConnectionId(connectionOrId) {
  if (typeof connectionOrId === "string") {
    const id = connectionOrId.trim();
    return id || null;
  }
  if (connectionOrId && typeof connectionOrId === "object") {
    const id = String(connectionOrId.id || "").trim();
    return id || null;
  }
  return null;
}

/**
 * Load the decrypted runtime configuration for a connection.
 * UI models with masked apiKey are never trusted — secrets are always re-fetched.
 *
 * @param {string|{ id: string }} connectionOrId
 * @returns {Promise<object|null>} normalized connection with includeSecrets=true
 */
export async function resolveConnectionRuntimeConfig(connectionOrId) {
  const connectionId = extractConnectionId(connectionOrId);
  if (!connectionId) return null;
  return getConnectionSecrets(connectionId);
}

/**
 * Whether the resolved connection has a live (unmasked) API key for SDK tests.
 */
export function hasLiveCredentials(conn) {
  if (!conn) return false;
  const key = String(conn.apiKey || "").trim();
  return Boolean(key) && key !== SECRET_MASK && !isMaskedOrEmpty(key);
}

/**
 * Firebase client config built from the resolved (decrypted) connection.
 * Same shape used by Verify, Deep Test, and the runtime engine.
 */
export function getResolvedFirebaseConfig(conn) {
  if (!conn) throw new Error("Connection configuration is required.");
  return buildFirebaseConfig(conn);
}
